import { z } from 'zod';

// Availability management schemas (Story #41)
export const AvailabilityTypeSchema = z.enum(['available', 'busy', 'maybe']);

export const RecurrencePatternSchema = z
  .object({
    type: z.enum(['weekly', 'monthly']),
    days: z.array(z.string()),
    until: z.date().optional(),
  })
  .optional();

// Time validation helpers
const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
const fifteenMinuteIncrements = /^([01]?[0-9]|2[0-3]):(00|15|30|45)$/;

export const DancerAvailabilitySchema = z
  .object({
    availableDate: z.date({
      error: (issue) =>
        issue.input === undefined ? 'Date is required' : 'Invalid date format',
    }),
    startTime: z
      .string()
      .regex(timeRegex, 'validation:invalidDate')
      .regex(fifteenMinuteIncrements, 'validation:invalidDate'),
    endTime: z
      .string()
      .regex(timeRegex, 'validation:invalidDate')
      .regex(fifteenMinuteIncrements, 'validation:invalidDate'),
    availabilityType: AvailabilityTypeSchema.default('available'),
    notes: z.string().max(500).optional(),
    isRecurring: z.boolean().default(false),
    recurrencePattern: RecurrencePatternSchema,
    timezone: z.string().default('UTC'),
  })
  .refine(
    (data) => {
      // Ensure end time is after start time
      const startParts = data.startTime.split(':').map(Number);
      const endParts = data.endTime.split(':').map(Number);

      if (startParts.length !== 2 || endParts.length !== 2) return false;

      const startHour = startParts[0];
      const startMin = startParts[1];
      const endHour = endParts[0];
      const endMin = endParts[1];

      if (
        startHour === undefined ||
        startMin === undefined ||
        endHour === undefined ||
        endMin === undefined
      )
        return false;
      if (
        isNaN(startHour) ||
        isNaN(startMin) ||
        isNaN(endHour) ||
        isNaN(endMin)
      )
        return false;

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      path: ['endTime'],
      error: 'validation:rules.endTimeMustBeAfterStartTime',
    },
  );

export const TimeSlotSchema = z
  .object({
    startTime: z
      .string()
      .regex(timeRegex, 'validation:invalidDate')
      .regex(fifteenMinuteIncrements, 'validation:invalidDate'),
    endTime: z
      .string()
      .regex(timeRegex, 'validation:invalidDate')
      .regex(fifteenMinuteIncrements, 'validation:invalidDate'),
    availabilityType: AvailabilityTypeSchema.default('available'),
    notes: z.string().max(500).optional(),
    isRecurring: z.boolean().default(false),
    recurrencePattern: RecurrencePatternSchema,
    timezone: z.string().default('UTC'),
  })
  .refine(
    (data) => {
      // Ensure end time is after start time
      const startParts = data.startTime.split(':').map(Number);
      const endParts = data.endTime.split(':').map(Number);

      if (startParts.length !== 2 || endParts.length !== 2) return false;

      const startHour = startParts[0];
      const startMin = startParts[1];
      const endHour = endParts[0];
      const endMin = endParts[1];

      if (
        startHour === undefined ||
        startMin === undefined ||
        endHour === undefined ||
        endMin === undefined
      )
        return false;
      if (
        isNaN(startHour) ||
        isNaN(startMin) ||
        isNaN(endHour) ||
        isNaN(endMin)
      )
        return false;

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      path: ['endTime'],
      error: 'validation:rules.endTimeMustBeAfterStartTime',
    },
  );

export const BulkAvailabilitySchema = z.object({
  dateRange: z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .refine((data) => data.to >= data.from, {
      path: ['to'],
      error: 'validation:rules.endDateMustBeAfterOrEqual',
    }),
  timeSlots: z.array(TimeSlotSchema).min(1),
  applyToWeekdays: z.array(z.number().min(0).max(6)).default([]),
  overwriteExisting: z.boolean().default(false),
});

export const TeamAvailabilityQuerySchema = z
  .object({
    teamId: z.guid(),
    startDate: z.date(),
    endDate: z.date(),
    minDuration: z.number().min(15).default(90), // minutes
    minDancersRequired: z.number().min(1).default(1),
  })
  .refine((data) => data.endDate >= data.startDate, {
    path: ['endDate'],
    error: 'validation:rules.endDateMustBeAfterOrEqual',
  });

// Base TimeSlot schema for partial updates (without refinement)
const TimeSlotUpdateSchema = z.object({
  startTime: z
    .string()
    .regex(timeRegex, 'validation:invalidDate')
    .regex(fifteenMinuteIncrements, 'validation:invalidDate')
    .optional(),
  endTime: z
    .string()
    .regex(timeRegex, 'validation:invalidDate')
    .regex(fifteenMinuteIncrements, 'validation:invalidDate')
    .optional(),
  availabilityType: AvailabilityTypeSchema.optional(),
  notes: z.string().max(500).optional(),
  isRecurring: z.boolean().optional(),
  recurrencePattern: RecurrencePatternSchema.optional(),
  timezone: z.string().optional(),
});

export const AvailabilitySlotUpdateSchema = z.object({
  id: z.guid(),
  updates: TimeSlotUpdateSchema,
});

// Quick availability preset schemas
export const QuickAvailabilityPresetSchema = z.object({
  name: z.string().min(1).max(50),
  timeSlots: z
    .array(
      z.object({
        startTime: z.string().regex(fifteenMinuteIncrements),
        endTime: z.string().regex(fifteenMinuteIncrements),
        availabilityType: AvailabilityTypeSchema,
      }),
    )
    .min(1),
  weekdays: z.array(z.number().min(0).max(6)).default([1, 2, 3, 4, 5]), // Mon-Fri default
});

// Type exports
export type DancerAvailabilityData = z.infer<typeof DancerAvailabilitySchema>;
export type TimeSlotData = z.infer<typeof TimeSlotSchema>;
export type BulkAvailabilityData = z.infer<typeof BulkAvailabilitySchema>;
export type TeamAvailabilityQueryData = z.infer<
  typeof TeamAvailabilityQuerySchema
>;
export type AvailabilitySlotUpdateData = z.infer<
  typeof AvailabilitySlotUpdateSchema
>;
export type QuickAvailabilityPresetData = z.infer<
  typeof QuickAvailabilityPresetSchema
>;
export type AvailabilityType = z.infer<typeof AvailabilityTypeSchema>;
export type RecurrencePattern = z.infer<typeof RecurrencePatternSchema>;
